/**
 * `PromotionType` — CONTRACTS §2.8, 9 mecanicas. `CUPOM` **nao** e mecanica de promocao
 * (cupom e agregado proprio, `Coupon`), mas **e** linha da matriz oferta x classe, porque
 * tem gate por classe (REG-030). Dai a distincao entre `PromotionType` e `OfferMechanic`.
 */

export const PROMOTION_TYPES = [
  'PRECO_FIXO',
  'PERCENTUAL',
  'VALOR_FIXO',
  'LEVE_X_PAGUE_Y',
  'PROGRESSIVO',
  'COMBO',
  'BRINDE',
  'FRETE_GRATIS',
  'CASHBACK',
] as const;

export type PromotionType = (typeof PROMOTION_TYPES)[number];

/**
 * CONTRACTS §9 — a dimensao canonica da matriz e **11 linhas**: 9 mecanicas de promocao,
 * `CUPOM` e `EQUALIZACAO_DE_REDE`.
 *
 * Os dois ultimos nao sao `PromotionType` e estao aqui pelo MESMO motivo: tem gate por classe.
 * `CUPOM` e agregado proprio (`Coupon`, REG-030); `EQUALIZACAO_DE_REDE` e derivada do catalogo
 * na hora da cotacao (ADR-0026) e nao tem cadastro nenhum. O que a matriz governa nao e "de onde
 * veio o desconto", e sim "esta classe pode receber desconto" — e ai os tres se parecem.
 */
export const OFFER_MECHANICS = [...PROMOTION_TYPES, 'CUPOM', 'EQUALIZACAO_DE_REDE'] as const;

export type OfferMechanic = (typeof OFFER_MECHANICS)[number];

/** CONTRACTS §2.8 — mecanicas ligadas no MVP. Fora daqui, negada por padrao. */
export const MVP_OFFER_MECHANICS = [
  'PRECO_FIXO',
  'PERCENTUAL',
  'FRETE_GRATIS',
] as const satisfies readonly OfferMechanic[];

/** Mecanicas de **volume** — impossiveis sobre medicamento e NBCAL (REG-031, CHECK no banco). */
export const VOLUME_MECHANICS = [
  'LEVE_X_PAGUE_Y',
  'PROGRESSIVO',
  'COMBO',
] as const satisfies readonly OfferMechanic[];

/** Mecanicas de **unidade** — etapa 4 da cascata (CONTRACTS §3.1). */
export const UNIT_MECHANICS = [
  'PRECO_FIXO',
  'PERCENTUAL',
  'VALOR_FIXO',
] as const satisfies readonly OfferMechanic[];

const CONJUNTO_MECANICAS: ReadonlySet<string> = new Set<string>(OFFER_MECHANICS);
const CONJUNTO_MVP: ReadonlySet<string> = new Set<string>(MVP_OFFER_MECHANICS);
const CONJUNTO_UNIDADE: ReadonlySet<string> = new Set<string>(UNIT_MECHANICS);
const CONJUNTO_VOLUME: ReadonlySet<string> = new Set<string>(VOLUME_MECHANICS);

export function isOfferMechanic(value: unknown): value is OfferMechanic {
  return typeof value === 'string' && CONJUNTO_MECANICAS.has(value);
}

export function isPromotionType(value: unknown): value is PromotionType {
  return isOfferMechanic(value) && value !== 'CUPOM';
}

export function isMvpOfferMechanic(value: string): boolean {
  return CONJUNTO_MVP.has(value);
}

export function isUnitMechanic(value: string): boolean {
  return CONJUNTO_UNIDADE.has(value);
}

export function isVolumeMechanic(value: string): boolean {
  return CONJUNTO_VOLUME.has(value);
}
